import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { FaSearch, FaExternalLinkAlt } from "react-icons/fa";
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import SetupContainer from "../../../setupPage/SharedComponents/SetupContainer";
import Box from "../../../setupPage/SharedComponents/Box";
import SetupTittle from "../../../setupPage/SharedComponents/SetupTittle";
import GetLocalStorageUserId from "../../../sharedComponents/GetLocalStorageuserId";
import { isAuthenticationError } from "../../../../utils/authToken";
import CarrierPartnerPointModal from "../../../setupPage/CarrierPartnerPoint/CarrierPartnerPointModal";
import {
  getCarrierPartners,
  getCarrierPartnerById,
  searchCarrierPartnerParcels,
  getParcelRouteForCarrierPartner,
  createCarrierPartner,
  updateCarrierPartner,
  deleteCarrierPartner,
  getCarrierPartnerPoints,
} from "../../../../api/carrierPartnerApi";

const THEME = {
  brown: "#1c1917",
  border: "#e7e5e4",
};

const emptyForm = {
  parcelId: "",
  recordSerialNo: "",
  senderName: "",
  routeId: "",
  routeName: "",
  carrierPartnerPointId: "",
  carrierPartnerName: "",
  charge: "",
  trackingLink: "",
};

const pointLabel = (row) => row?.pointName || row?.detailsName || "—";

const getAdminUserId = () => {
  try {
    return String(GetLocalStorageUserId() || "");
  } catch {
    return "";
  }
};

const formatDateTime = (value) => {
  if (!value || value === "0001-01-01T00:00:00") return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
};

const parcelLabel = (row) => {
  if (!row) return "";
  const parts = [];
  if (row.parcelId != null && row.parcelId !== "") parts.push(`#${row.parcelId}`);
  if (row.recordSerialNo) parts.push(row.recordSerialNo);
  if (row.senderName) parts.push(row.senderName);
  return parts.join(" · ");
};

const toTrackingHref = (link) => {
  const raw = String(link || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
};

const apiError = (err, fallback) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  return data?.title || data?.message || err?.message || fallback;
};

const toastApiError = (err, fallback) => {
  if (isAuthenticationError(err)) return;
  toast.error(apiError(err, fallback));
};

const isValidOptionalUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return true;
  try {
    const href = toTrackingHref(raw);
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

const AdminCarrierPartner = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [parcelQuery, setParcelQuery] = useState("");
  const [parcelOptions, setParcelOptions] = useState([]);
  const [parcelSearching, setParcelSearching] = useState(false);
  const [parcelMenuOpen, setParcelMenuOpen] = useState(false);
  const parcelBoxRef = useRef(null);
  const debounceRef = useRef(null);

  const [points, setPoints] = useState([]);
  const [pointModalOpen, setPointModalOpen] = useState(false);
  const [routeLoading, setRouteLoading] = useState(false);
  const routeRequestRef = useRef(0);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCarrierPartners();
      setRows(data);
    } catch (err) {
      toastApiError(err, "Failed to load carrier partners.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPoints = useCallback(async () => {
    try {
      const data = await getCarrierPartnerPoints();
      setPoints(data);
    } catch (err) {
      toastApiError(err, "Failed to load point names.");
      setPoints([]);
    }
  }, []);

  useEffect(() => {
    fetchRows();
    fetchPoints();
  }, [fetchRows, fetchPoints]);

  const activePoints = useMemo(
    () => points.filter((p) => p.isActive),
    [points],
  );

  const dropdownPoints = useMemo(() => {
    const selectedId = Number(form.carrierPartnerPointId);
    const list = [...activePoints];
    if (selectedId && !list.some((p) => p.id === selectedId)) {
      const current = points.find((p) => p.id === selectedId);
      if (current) list.push(current);
    }
    return list;
  }, [activePoints, form.carrierPartnerPointId, points]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!parcelBoxRef.current?.contains(e.target)) {
        setParcelMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = parcelQuery.trim();
    const selectedLabel = parcelLabel({
      parcelId: form.parcelId,
      recordSerialNo: form.recordSerialNo,
      senderName: form.senderName,
    });
    if (q.length < 2 || (form.parcelId && q === selectedLabel)) {
      setParcelOptions([]);
      setParcelSearching(false);
      return undefined;
    }

    setParcelSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const options = await searchCarrierPartnerParcels(q);
        setParcelOptions(options);
      } catch (err) {
        toastApiError(err, "Failed to search parcels.");
        setParcelOptions([]);
      } finally {
        setParcelSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [parcelQuery, form.parcelId, form.recordSerialNo, form.senderName]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setParcelQuery("");
    setParcelOptions([]);
    setParcelMenuOpen(false);
    setRouteLoading(false);
  };

  const applyParcelRoute = useCallback(async (parcelId, known = {}) => {
    const requestId = ++routeRequestRef.current;
    const knownName = String(known.routeName || "").trim();
    const knownId = known.routeId || "";
    if (knownName) {
      setForm((prev) => ({
        ...prev,
        routeId: knownId,
        routeName: knownName,
      }));
      setRouteLoading(false);
      return;
    }

    setRouteLoading(true);
    try {
      const route = await getParcelRouteForCarrierPartner(parcelId);
      if (requestId !== routeRequestRef.current) return;
      setForm((prev) => {
        if (String(prev.parcelId) !== String(parcelId)) return prev;
        return {
          ...prev,
          routeId: route.routeId || knownId || "",
          routeName: route.routeName || "",
        };
      });
    } catch {
      if (requestId !== routeRequestRef.current) return;
      setForm((prev) => {
        if (String(prev.parcelId) !== String(parcelId)) return prev;
        return { ...prev, routeName: prev.routeName || "" };
      });
    } finally {
      if (requestId === routeRequestRef.current) setRouteLoading(false);
    }
  }, []);

  const handleSelectParcel = (option) => {
    setForm((prev) => ({
      ...prev,
      parcelId: option.parcelId,
      recordSerialNo: option.recordSerialNo || "",
      senderName: option.senderName || "",
      routeId: option.routeId || "",
      routeName: option.routeName || "",
    }));
    setParcelQuery(parcelLabel(option));
    setParcelMenuOpen(false);
    applyParcelRoute(option.parcelId, option);
  };

  const handleEdit = async (row) => {
    setEditingId(row.id);
    setForm({
      parcelId: row.parcelId ?? "",
      recordSerialNo: row.recordSerialNo || "",
      senderName: row.senderName || "",
      routeId: row.routeId || "",
      routeName: row.routeName || "",
      carrierPartnerPointId: row.carrierPartnerPointId || "",
      carrierPartnerName: row.carrierPartnerName || "",
      charge: row.charge != null ? String(row.charge) : "",
      trackingLink: row.trackingLink || "",
    });
    setParcelQuery(parcelLabel(row));
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (row.parcelId) applyParcelRoute(row.parcelId, row);

    try {
      const fresh = await getCarrierPartnerById(row.id);
      if (!fresh) return;
      setForm((prev) => ({
        parcelId: fresh.parcelId ?? row.parcelId ?? "",
        recordSerialNo: fresh.recordSerialNo || row.recordSerialNo || "",
        senderName: fresh.senderName || row.senderName || "",
        routeId: prev.routeId || fresh.routeId || row.routeId || "",
        routeName: prev.routeName || fresh.routeName || row.routeName || "",
        carrierPartnerPointId:
          fresh.carrierPartnerPointId || row.carrierPartnerPointId || "",
        carrierPartnerName: fresh.carrierPartnerName || "",
        charge: fresh.charge != null ? String(fresh.charge) : "",
        trackingLink: fresh.trackingLink || "",
      }));
      setParcelQuery(parcelLabel(fresh.parcelId ? fresh : row));
      const parcelId = fresh.parcelId ?? row.parcelId;
      if (parcelId) applyParcelRoute(parcelId, fresh.routeName ? fresh : row);
    } catch {
      /* keep list-row values */
    }
  };

  const handleDelete = async (row) => {
    const confirm = await Swal.fire({
      title: "Delete carrier partner?",
      text: `Remove ${row.carrierPartnerName || "this record"} for parcel #${row.parcelId}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#dc2626",
    });
    if (!confirm.isConfirmed) return;

    try {
      await deleteCarrierPartner(row.id, getAdminUserId());
      toast.success("Carrier partner deleted.");
      if (editingId === row.id) resetForm();
      await fetchRows();
    } catch (err) {
      toastApiError(err, "Failed to delete carrier partner.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const adminId = getAdminUserId();
    if (!adminId) {
      toast.error("Admin session not found. Please sign in again.");
      return;
    }
    if (!form.parcelId) {
      toast.error("Select a parcel / booking number.");
      return;
    }
    if (!form.carrierPartnerPointId) {
      toast.error("Select what this carrier is for.");
      return;
    }
    if (!String(form.carrierPartnerName || "").trim()) {
      toast.error("Carrier partner name is required.");
      return;
    }
    const chargeNum = Number(form.charge);
    if (!Number.isFinite(chargeNum) || chargeNum < 0) {
      toast.error("Charge must be a number of 0 or more.");
      return;
    }
    if (!isValidOptionalUrl(form.trackingLink)) {
      toast.error("Enter a valid tracking URL, or leave it blank.");
      return;
    }

    const payload = {
      parcelId: Number(form.parcelId),
      carrierPartnerPointId: Number(form.carrierPartnerPointId),
      carrierPartnerName: String(form.carrierPartnerName).trim(),
      charge: chargeNum,
      trackingLink: String(form.trackingLink || "").trim() || null,
      creatorId: adminId,
      modifierId: adminId,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateCarrierPartner(editingId, { ...payload, id: editingId });
        toast.success("Carrier partner updated.");
      } else {
        await createCarrierPartner(payload);
        toast.success("Carrier partner saved.");
      }
      resetForm();
      await fetchRows();
    } catch (err) {
      toastApiError(err, "Failed to save carrier partner.");
    } finally {
      setSaving(false);
    }
  };

  const showParcelMenu =
    parcelMenuOpen &&
    parcelQuery.trim().length >= 2 &&
    parcelQuery.trim() !==
      parcelLabel({
        parcelId: form.parcelId,
        recordSerialNo: form.recordSerialNo,
        senderName: form.senderName,
      });

  const handlePointsChanged = (list, created) => {
    if (Array.isArray(list)) setPoints(list);
    if (created?.id) {
      setForm((prev) => ({
        ...prev,
        carrierPartnerPointId: String(created.id),
      }));
    }
  };

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => {
      const ta = a.creationDate ? new Date(a.creationDate).getTime() : 0;
      const tb = b.creationDate ? new Date(b.creationDate).getTime() : 0;
      return tb - ta;
    });
  }, [rows]);

  return (
    <SetupContainer>
      <Box top>
        <SetupTittle text="Carrier Partner" />
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <p className="text-stone-600">
            Assign a carrier partner, what the assignment is for, charge, and
            tracking link to a parcel. Admin only — not shown to customers or
            agents.
          </p>
          <button
            type="button"
            onClick={fetchRows}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-white font-medium hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: THEME.brown }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm mb-6"
        >
          <div className="flex items-center justify-between gap-3 mb-4">
            <p className="text-[11px] font-bold uppercase tracking-wide text-stone-500">
              {editingId ? "Edit carrier partner" : "Create carrier partner"}
            </p>
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                className="text-sm font-semibold text-stone-600 hover:underline"
              >
                Cancel edit
              </button>
            ) : null}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative" ref={parcelBoxRef}>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
                Parcel / Booking No
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
                <input
                  type="text"
                  value={parcelQuery}
                  onChange={(e) => {
                    const next = e.target.value;
                    setParcelQuery(next);
                    setParcelMenuOpen(true);
                    if (form.parcelId && next.trim() !== parcelLabel(form)) {
                      setForm((prev) => ({
                        ...prev,
                        parcelId: "",
                        recordSerialNo: "",
                        senderName: "",
                        routeId: "",
                        routeName: "",
                      }));
                    }
                  }}
                  onFocus={() => {
                    if (parcelQuery.trim().length >= 2) setParcelMenuOpen(true);
                  }}
                  placeholder="Search parcel id, serial, or sender (≥ 2 chars)"
                  className="w-full h-11 pl-9 pr-3 rounded-lg border-2 outline-none bg-white text-sm font-medium"
                  style={{ borderColor: THEME.border }}
                  autoComplete="off"
                />
              </div>
              {showParcelMenu ? (
                <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-lg">
                  {parcelSearching ? (
                    <li className="px-3 py-3 text-sm text-stone-500">
                      Searching…
                    </li>
                  ) : parcelOptions.length === 0 ? (
                    <li className="px-3 py-3 text-sm text-stone-500">
                      No parcels found.
                    </li>
                  ) : (
                    parcelOptions.map((opt) => (
                      <li key={opt.parcelId}>
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2.5 hover:bg-amber-50 border-b border-stone-100 last:border-0"
                          onClick={() => handleSelectParcel(opt)}
                        >
                          <p className="font-semibold text-stone-900 text-sm">
                            #{opt.parcelId}
                            {opt.recordSerialNo ? ` · ${opt.recordSerialNo}` : ""}
                          </p>
                          <p className="text-xs text-stone-500 truncate">
                            {opt.senderName || "No sender name"}
                          </p>
                          {opt.routeName ? (
                            <p className="text-xs text-stone-700 truncate">
                              Route: {opt.routeName}
                            </p>
                          ) : null}
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              ) : null}
              {form.parcelId ? (
                <div className="mt-3">
                  <label className="block text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
                    Route
                  </label>
                  <div
                    className="w-full min-h-11 px-3 py-2.5 rounded-lg border-2 bg-stone-50 text-sm font-medium text-stone-900"
                    style={{ borderColor: THEME.border }}
                  >
                    {routeLoading && !form.routeName
                      ? "Loading route…"
                      : form.routeName || "No route on this parcel"}
                  </div>
                </div>
              ) : null}
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
                Point name <span className="text-red-600">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  required
                  value={form.carrierPartnerPointId}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      carrierPartnerPointId: e.target.value,
                    }))
                  }
                  className="flex-1 min-w-0 h-11 px-3 rounded-lg border-2 outline-none bg-white text-sm font-medium"
                  style={{ borderColor: THEME.border }}
                >
                  <option value="">Select what this carrier is for</option>
                  {dropdownPoints.map((point) => (
                    <option key={point.id} value={point.id}>
                      {point.name}
                      {point.isActive ? "" : " (inactive)"}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setPointModalOpen(true)}
                  className="h-11 w-11 shrink-0 rounded-lg text-white hover:opacity-90 flex items-center justify-center"
                  style={{ backgroundColor: THEME.brown }}
                  title="Set up point names"
                  aria-label="Set up point names"
                >
                  <FiPlus className="text-xl" />
                </button>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
                Carrier partner name <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                required
                value={form.carrierPartnerName}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    carrierPartnerName: e.target.value,
                  }))
                }
                placeholder="e.g. DHL, FedEx"
                className="w-full h-11 px-3 rounded-lg border-2 outline-none bg-white text-sm font-medium"
                style={{ borderColor: THEME.border }}
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
                Price / parcel charge
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.charge}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, charge: e.target.value }))
                }
                placeholder="0.00"
                className="w-full h-11 px-3 rounded-lg border-2 outline-none bg-white text-sm font-medium"
                style={{ borderColor: THEME.border }}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-[11px] font-bold uppercase tracking-wide text-stone-500 mb-1">
                Parcel tracking link
              </label>
              <input
                type="text"
                inputMode="url"
                value={form.trackingLink}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, trackingLink: e.target.value }))
                }
                placeholder="https://…"
                className="w-full h-11 px-3 rounded-lg border-2 outline-none bg-white text-sm font-medium"
                style={{ borderColor: THEME.border }}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 rounded-lg border border-stone-300 bg-white font-medium hover:bg-stone-50"
            >
              Clear
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 rounded-lg text-white font-semibold hover:opacity-90 disabled:opacity-60"
              style={{ backgroundColor: THEME.brown }}
            >
              {saving ? "Saving…" : editingId ? "Update" : "Save"}
            </button>
          </div>
        </form>

        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#f7f1ec] text-[11px] uppercase tracking-wide text-stone-600">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">
                    Parcel / Booking no
                  </th>
                  <th className="px-4 py-3 text-left font-bold">Point name</th>
                  <th className="px-4 py-3 text-left font-bold">
                    Carrier partner
                  </th>
                  <th className="px-4 py-3 text-right font-bold">Charge</th>
                  <th className="px-4 py-3 text-left font-bold">Tracking link</th>
                  <th className="px-4 py-3 text-left font-bold">Date</th>
                  <th className="px-4 py-3 text-left font-bold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading && sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-stone-500">
                      Loading carrier partners…
                    </td>
                  </tr>
                ) : sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-stone-500">
                      No carrier partners yet.
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((row) => {
                    const href = toTrackingHref(row.trackingLink);
                    return (
                      <tr key={row.id} className="hover:bg-stone-50">
                        <td className="px-4 py-3 align-top">
                          <p className="font-semibold text-stone-900">
                            #{row.parcelId ?? "—"}
                          </p>
                          {row.recordSerialNo ? (
                            <p className="text-[11px] font-mono text-stone-500 break-all">
                              {row.recordSerialNo}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3 align-top text-stone-800">
                          {pointLabel(row)}
                        </td>
                        <td className="px-4 py-3 align-top font-semibold text-stone-900">
                          {row.carrierPartnerName || "—"}
                        </td>
                        <td className="px-4 py-3 align-top text-right tabular-nums font-semibold text-stone-900 whitespace-nowrap">
                          {Number(row.charge || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 align-top">
                          {href ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-blue-700 font-medium hover:underline break-all"
                            >
                              Open
                              <FaExternalLinkAlt className="text-[10px] shrink-0" />
                            </a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-4 py-3 align-top text-stone-700 whitespace-nowrap">
                          {formatDateTime(row.creationDate)}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleEdit(row)}
                              className="inline-flex items-center gap-1 text-sm font-semibold text-stone-700 hover:underline"
                            >
                              <FiEdit2 />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(row)}
                              className="inline-flex items-center gap-1 text-sm font-semibold text-red-700 hover:underline"
                            >
                              <FiTrash2 />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Box>
      <CarrierPartnerPointModal
        open={pointModalOpen}
        onClose={() => setPointModalOpen(false)}
        onChanged={handlePointsChanged}
      />
    </SetupContainer>
  );
};

export default AdminCarrierPartner;
